/**
 * Normalize common LaTeX math delimiters to forms that remark-math understands
 * with `singleDollarTextMath: false` (only `$$…$$`).
 *
 * Why: LLMs and math tutors default to `\[…\]` / `\(…\)` (standard LaTeX) and
 * also still emit TeX-style `$…$`. CommonMark treats `\[` as a literal `[`, and
 * remark-math with single-dollar disabled leaves `$…$` as raw text — so
 * equations show as unrendered TeX (`$\int …$`). Currency agents still need
 * bare `$1.2M` to stay text — only *math-looking* single-dollar pairs convert.
 *
 * Converts (outside fenced / inline code only):
 *   \[ … \]  →  $$\n…\n$$   (display)
 *   \( … \)  →  $$…$$       (inline; same-line $$ is inline under remark-math)
 *   $ … $    →  $$…$$       (inline; only when body looks like LaTeX/math)
 *
 * Display-math indentation matters: when `\[…\]` sits inside a container
 * (list item, blockquote), the closing `$$` fence must carry the same line
 * prefix. A closing fence emitted at column 0 breaks out of the container,
 * becomes a NEW math opener, and shifts `$$` pairing for the rest of the
 * message — every later paragraph renders as raw red KaTeX-error text.
 * A `\[…\]` that starts mid-line cannot be block math at all; it degrades
 * to same-line `$$…$$` (inline) instead of a broken block.
 *
 * Incomplete pairs (streaming) are left unchanged.
 */

export function normalizeMathDelimiters(md: string): string {
  if (!md.includes('\\[') && !md.includes('\\(') && !md.includes('$')) {
    return md;
  }

  const segments = splitPreservingCode(md);
  let out = '';
  for (const seg of segments) {
    out += seg.kind === 'code' ? seg.value : transformMathInText(seg.value);
  }
  return out;
}

type Segment = { kind: 'code' | 'text'; value: string };

/** True when index is at the start of the string or immediately after `\n`. */
function isLineStart(s: string, i: number): boolean {
  return i === 0 || s[i - 1] === '\n';
}

/**
 * Split markdown into code vs text segments so math rewrite cannot touch
 * fenced blocks or inline code (where `\[` may appear as examples).
 */
function splitPreservingCode(md: string): Segment[] {
  const segs: Segment[] = [];
  let i = 0;
  let textBuf = '';

  const flushText = (): void => {
    if (textBuf.length > 0) {
      segs.push({ kind: 'text', value: textBuf });
      textBuf = '';
    }
  };

  while (i < md.length) {
    // Fenced code: ``` or ~~~ at line start (GFM).
    if (isLineStart(md, i) && (md.startsWith('```', i) || md.startsWith('~~~', i))) {
      flushText();
      const fenceChar = md[i]!;
      let openLen = 0;
      while (i + openLen < md.length && md[i + openLen] === fenceChar) {
        openLen++;
      }
      // Consume opening fence line through newline (or EOS).
      let j = i + openLen;
      while (j < md.length && md[j] !== '\n') j++;
      if (j < md.length && md[j] === '\n') j++;

      // Find closing fence of at least openLen same chars on its own line.
      let closed = false;
      while (j < md.length) {
        if (isLineStart(md, j)) {
          let k = 0;
          while (j + k < md.length && md[j + k] === fenceChar) k++;
          if (k >= openLen) {
            let end = j + k;
            while (end < md.length && md[end] !== '\n' && /\s/.test(md[end]!)) {
              end++;
            }
            if (end >= md.length || md[end] === '\n') {
              if (end < md.length && md[end] === '\n') end++;
              segs.push({ kind: 'code', value: md.slice(i, end) });
              i = end;
              closed = true;
              break;
            }
          }
        }
        j++;
      }
      if (!closed) {
        // Unclosed fence (streaming): protect rest as code so we do not rewrite.
        segs.push({ kind: 'code', value: md.slice(i) });
        return segs;
      }
      continue;
    }

    // Inline code: one or more backticks (CommonMark codespan).
    if (md[i] === '`') {
      flushText();
      let openLen = 0;
      while (i + openLen < md.length && md[i + openLen] === '`') openLen++;
      const closer = '`'.repeat(openLen);
      const closeAt = md.indexOf(closer, i + openLen);
      if (closeAt === -1) {
        segs.push({ kind: 'code', value: md.slice(i) });
        return segs;
      }
      const end = closeAt + openLen;
      segs.push({ kind: 'code', value: md.slice(i, end) });
      i = end;
      continue;
    }

    textBuf += md[i];
    i++;
  }

  flushText();
  return segs;
}

function transformMathInText(text: string): string {
  let out = '';
  let i = 0;
  while (i < text.length) {
    if (text.startsWith('\\[', i)) {
      const end = indexOfUnescapedClose(text, i + 2, '\\]');
      if (end === -1) {
        out += text.slice(i);
        break;
      }
      const body = text.slice(i + 2, end);
      out += wrapDisplay(body, linePrefixOf(out));
      i = end + 2;
      continue;
    }
    if (text.startsWith('\\(', i)) {
      const end = indexOfUnescapedClose(text, i + 2, '\\)');
      if (end === -1) {
        out += text.slice(i);
        break;
      }
      const body = text.slice(i + 2, end);
      out += wrapInline(body);
      i = end + 2;
      continue;
    }
    // Pass through existing $$…$$ so we never split a double-dollar fence.
    if (text.startsWith('$$', i)) {
      const end = text.indexOf('$$', i + 2);
      if (end === -1) {
        out += text.slice(i);
        break;
      }
      out += text.slice(i, end + 2);
      i = end + 2;
      continue;
    }
    // TeX-style $…$ (LLMs' default). Convert only when body looks like math;
    // leave currency ($1.2M) and prose alone. \$ is not an opener.
    //
    // Critical: if the nearest closer does *not* form math, emit only this `$`
    // and keep scanning — do not consume the closer. Otherwise currency like
    // `$1.675T and math $x^2$` steals the `$` of `$x^2$` and leaves later
    // `\[…\]` unprocessed (early `break` on the dangling remainder).
    if (text[i] === '$' && !isEscapedDollar(text, i)) {
      const end = indexOfUnescapedSingleDollar(text, i + 1);
      if (end === -1) {
        out += text.slice(i);
        break;
      }
      // Do not treat $…$$ as a single-dollar pair (closing would steal one $ of $$).
      if (text[end + 1] === '$') {
        out += text[i];
        i++;
        continue;
      }
      const body = text.slice(i + 1, end);
      // Cap length so a stray `$` cannot swallow a whole section before the
      // next `$`. Non-math bodies do not consume the closer (see above).
      if (body.length <= 400 && looksLikeLatexMath(body)) {
        out += wrapInline(body);
        i = end + 1;
        continue;
      }
      out += '$';
      i++;
      continue;
    }
    out += text[i];
    i++;
  }
  return out;
}

/** Find `\\]` or `\\)` that is not preceded by an odd extra backslash run. */
function indexOfUnescapedClose(s: string, from: number, close: '\\]' | '\\)'): number {
  let i = from;
  while (i < s.length) {
    const at = s.indexOf(close, i);
    if (at === -1) return -1;
    // Count consecutive backslashes immediately before the close marker's `\`.
    // close is two chars: `\` + `]`/`)`. We want the LaTeX closer `\]`, not `\\]`.
    let bs = 0;
    let k = at - 1;
    while (k >= from && s[k] === '\\') {
      bs++;
      k--;
    }
    // `at` points at `\`. If there is an even number of extra `\` before that
    // delimiter backslash (bs===0,2,4…), the delimiter is active.
    if (bs % 2 === 0) return at;
    i = at + 1;
  }
  return -1;
}

/** True when `$` at index is escaped as `\$` (odd run of backslashes before it). */
function isEscapedDollar(s: string, at: number): boolean {
  let bs = 0;
  let k = at - 1;
  while (k >= 0 && s[k] === '\\') {
    bs++;
    k--;
  }
  return bs % 2 === 1;
}

/** Next unescaped single `$` that is not the start of `$$`. */
function indexOfUnescapedSingleDollar(s: string, from: number): number {
  let i = from;
  while (i < s.length) {
    const at = s.indexOf('$', i);
    if (at === -1) return -1;
    if (isEscapedDollar(s, at)) {
      i = at + 1;
      continue;
    }
    // Skip the first $ of a $$ run (display fence).
    if (s[at + 1] === '$') {
      i = at + 2;
      continue;
    }
    return at;
  }
  return -1;
}

/**
 * Heuristic: body of `$…$` is math worth rendering, not currency/prose.
 * Safe defaults for finance agents: bare `$1.2M` / `$14.7B` never match
 * (usually no closing `$`; even with one, digits+unit fails this check).
 *
 * Weak signals (prime, slash) only apply to short/non-prose bodies. Otherwise
 * English between two currency dollars — e.g. `$560 is where … that's … $555`
 * — was misclassified as math because contractions match `f'` and KaTeX
 * collapses spaces into italic run-on text (invage marina META chat).
 */
export function looksLikeLatexMath(body: string): boolean {
  const t = body.trim();
  if (t.length === 0) return false;
  // Pure money-ish: 1.2M, 14.7B, 100, 1,234.56, 50%
  if (/^[\d,.\s]+[BMTkK%]?$/.test(t)) return false;
  // Price fragments with dash/en-dash: 550–, 550-560, 14.5–15
  if (/^[\d,.\s]+[–—\-][\d,.\s]*[BMTkK%]?$/.test(t)) return false;

  // Strong math signals — safe even inside multi-word bodies.
  // LaTeX command (\int, \frac, \ln, \to, \mathbf, …)
  if (/\\[a-zA-Z]+/.test(t)) return true;
  // TeX structure: superscript / braces always count.
  if (/[\^{}]/.test(t)) return true;
  // Subscripts: x_1, a_{ij}, x_i — NOT snake_case ids (jude_futu, cost_basis).
  // Bare `_` used to match /[\^_{}]/ and turn finance prose between two $
  // amounts into KaTeX (spaces collapse → "541.88onjudefutu…").
  if (hasTexSubscript(t)) return true;
  // Common math relations / operators
  if (/[=<>≤≥≈≠±·×÷∞]/.test(t)) return true;
  // Absolute value |x|, |f|
  if (/\|[^|\n]+\|/.test(t)) return true;
  // Greek letters often used bare
  if (/[α-ωΑ-Ω]/.test(t)) return true;

  // Weak signals only when the body is not multi-word English prose.
  // Contractions ("that's", "don't") and finance shorthand ("P/E", "buy/sell")
  // must not promote a whole sentence between two $ amounts to math.
  if (isMultiWordProse(t)) return false;

  // Derivatives: f', y'', f'(x) — not contractions (letter after ')
  if (/(?:^|[^a-zA-Z])[a-zA-Z]+'(?![a-zA-Z])/.test(t)) return true;
  // Short quotients of identifiers: f/g, dy/dx — not "P/E at" in prose
  if (/(?:^|[^a-zA-Z])[a-zA-Z]{1,4}\/[a-zA-Z]{1,4}(?:[^a-zA-Z]|$)/.test(t)) {
    return true;
  }
  return false;
}

/** True when body has 3+ alphabetic words (likely English, not $f'$ / $dy/dx$). */
function isMultiWordProse(t: string): boolean {
  const words = t.match(/[A-Za-z]{2,}/g);
  return words !== null && words.length >= 3;
}

/**
 * TeX subscript (not snake_case). Accept:
 *   - _{…} group: a_{ij}, x_{n+1}
 *   - _digit: x_1, a_12
 *   - single-letter base _ identifier: x_i, F_n, x_max
 * Reject multi-letter_left snake_case: jude_futu, cost_basis, avg_price.
 */
function hasTexSubscript(t: string): boolean {
  if (/_\s*\{/.test(t)) return true;
  if (/_\d/.test(t)) return true;
  // Single-letter math variable as base (char before base is non-alnum or BOS).
  if (/(?:^|[^a-zA-Z0-9])[a-zA-Z]_[a-zA-Z0-9]/.test(t)) return true;
  return false;
}

/**
 * Line prefix (indent / blockquote markers) already emitted for the line the
 * `\[…\]` opener sits on. Only whitespace and `>` count — anything else means
 * the opener is mid-line and cannot start block math.
 */
function linePrefixOf(emitted: string): string | null {
  const lineStart = emitted.lastIndexOf('\n') + 1;
  const prefix = emitted.slice(lineStart);
  return /^[ \t>]*$/.test(prefix) ? prefix : null;
}

function wrapDisplay(body: string, linePrefix: string | null): string {
  if (linePrefix === null) {
    // Mid-line \[…\]: block math is impossible here. Degrade to same-line
    // inline $$…$$ so no stray block fence can corrupt later $$ pairing.
    return `$$${body.trim().replace(/\s+/g, ' ')}$$`;
  }
  // Strip trailing whitespace-only lines (and a lone trailing blockquote
  // marker) so the closing fence is not pushed onto an over-indented line;
  // keep content-line indentation intact.
  const trimmed = body
    .replace(/^\n+/, '')
    .replace(/[ \t\n]+$/, '')
    .replace(/\n[ \t>]*$/, '');
  // The opener `$$` follows the already-emitted prefix; the closing fence
  // must repeat it to stay inside the same list/blockquote container.
  return `$$\n${trimmed}\n${linePrefix}$$`;
}

function wrapInline(body: string): string {
  return `$$${body.trim()}$$`;
}
